import { useEffect, useRef } from "react";

/**
 * Keeps wheel and trackpad scrolling inside the element it's attached to.
 *
 * The ad canvas listens for `wheel` natively on its container and
 * `preventDefault`s every one to pan and zoom the artboard. Anything scrollable
 * rendered *inside* that container — the thread card anchored to a pin — never
 * receives the gesture: the canvas pans instead, and the card only scrolls if
 * you drag its scrollbar by hand.
 *
 * A React `onWheel` can't fix that. Synthetic events are delegated at the root,
 * so the canvas's native listener has already run and preventDefaulted by the
 * time a synthetic handler could stop anything. The listener has to be native
 * too, and non-passive for the same reason the canvas's is.
 *
 * Scrolling is left to the browser — this only stops the event from travelling
 * further, so the element scrolls exactly as it would anywhere else, including
 * momentum and overscroll.
 */
export function useOwnWheel<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const onWheel = (event: WheelEvent) => event.stopPropagation();

    element.addEventListener("wheel", onWheel, { passive: false });
    return () => element.removeEventListener("wheel", onWheel);
  }, []);

  return ref;
}
